import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import costsReducer from './costsSlice';
import userReducer from './userSlice';
import reorderReducer from './reorderSlice';
import inventoryDraftsReducer from './inventoryDraftSlice';
import storeRegulationReducer from './storeRegulationSlice';
import cashFlowReducer from './cashFlowSlice';
import recentResourcesReducer from './recentResourcesSlice';
import hotMemoryReducer from './hotMemorySlice';
import harvenChatReducer from './harvenChatSlice';
import supplyNotificationsReducer from './supplyNotificationsSlice';
import {
  loadCostsFromLocalStorage,
  saveCostsToLocalStorage,
  loadInventoryDraftsFromLocalStorage,
  saveInventoryDraftsToLocalStorage,
  loadStoreRegulationFromLocalStorage,
  saveStoreRegulationToLocalStorage,
  loadCashFlowFromLocalStorage,
  saveCashFlowToLocalStorage,
  loadHotMemoryFromLocalStorage,
  saveHotMemoryToLocalStorage,
  loadHarvenChatFromLocalStorage,
  saveHarvenChatToLocalStorage,
  loadSupplyNotificationsFromLocalStorage,
  saveSupplyNotificationsToLocalStorage,
} from './localStorageUtils';

// Fonctions pour sauvegarder/charger l'utilisateur depuis localStorage
const loadUserFromLocalStorage = () => {
  try {
    const serializedUser = localStorage.getItem('user');
    if (serializedUser === null) {
      return undefined;
    }
    return JSON.parse(serializedUser);
  } catch (err) {
    console.error('Erreur lors du chargement de l\'utilisateur depuis localStorage:', err);
    return undefined;
  }
};

const saveUserToLocalStorage = (user: any) => {
  try {
    const serializedUser = JSON.stringify(user);
    localStorage.setItem('user', serializedUser);
  } catch (err) {
    console.error('Erreur lors de la sauvegarde de l\'utilisateur dans localStorage:', err);
  }
};

const loadRecentResourcesFromLocalStorage = () => {
  try {
    const serialized = localStorage.getItem('recentResources');
    if (serialized === null) {
      return undefined;
    }
    return JSON.parse(serialized);
  } catch (err) {
    console.error('Erreur lors du chargement des ressources récentes depuis localStorage:', err);
    return undefined;
  }
};

const saveRecentResourcesToLocalStorage = (state: any) => {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem('recentResources', serialized);
  } catch (err) {
    console.error('Erreur lors de la sauvegarde des ressources récentes dans localStorage:', err);
  }
};

// Charger l'état initial depuis le localStorage
const preloadedState = {
  costs: loadCostsFromLocalStorage() || { locations: {} },
  user:
    loadUserFromLocalStorage() || {
      currentUser: null,
      isAuthenticated: false,
      loading: false,
      error: null,
    },
  recentResources: loadRecentResourcesFromLocalStorage() || { itemsByUser: {} },
  hotMemory: loadHotMemoryFromLocalStorage() || { itemsByUser: {} },
  harvenChat: loadHarvenChatFromLocalStorage() || { messagesByUser: {} },
  supplyNotifications: loadSupplyNotificationsFromLocalStorage() || { items: [] },
  reorder: { locationReorderDataArray: [], currentLocation: "" },
  inventoryDrafts: loadInventoryDraftsFromLocalStorage() || undefined,
  storeRegulation: loadStoreRegulationFromLocalStorage() || { templates: {} },
  cashFlow: loadCashFlowFromLocalStorage() || {
    currentBalance: 0,
    initialBalance: 5000,
  },
};

const rootReducer = combineReducers({
  costs: costsReducer,
  user: userReducer,
  recentResources: recentResourcesReducer,
  hotMemory: hotMemoryReducer,
  harvenChat: harvenChatReducer,
  supplyNotifications: supplyNotificationsReducer,
  reorder: reorderReducer,
  inventoryDrafts: inventoryDraftsReducer,
  storeRegulation: storeRegulationReducer,
  cashFlow: cashFlowReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

// Créer le store avec l'état préchargé depuis le localStorage
export const store = configureStore({
  reducer: rootReducer,
  preloadedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

// S'abonner aux changements du store pour sauvegarder l'état dans le localStorage
store.subscribe(() => {
  const state = store.getState();
  saveCostsToLocalStorage(state.costs);
  
  // Sauvegarder l'utilisateur si authentifié
  if (state.user && state.user.isAuthenticated) {
    saveUserToLocalStorage(state.user);
  }
  // Toujours sauvegarder les brouillons d'inventaire (ne pas les vider au logout)
  saveInventoryDraftsToLocalStorage(state.inventoryDrafts);
  // Sauvegarder les templates de régulation des boutiques
  saveStoreRegulationToLocalStorage(state.storeRegulation);
  // Sauvegarder l'état du cash flow (solde initial + courant)
  saveCashFlowToLocalStorage(state.cashFlow);
  saveRecentResourcesToLocalStorage(state.recentResources);
  saveHotMemoryToLocalStorage(state.hotMemory);
  saveHarvenChatToLocalStorage(state.harvenChat);
  saveSupplyNotificationsToLocalStorage(state.supplyNotifications);
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
